/**
 * Email Deliverability & Spam Checker
 *
 * Analyzes rendered newsletter HTML and subject line for issues
 * that could trigger spam filters or cause rendering problems
 * across email clients.
 */

export type WarningSeverity = 'high' | 'medium' | 'low';

export interface DeliverabilityWarning {
  id: string;
  severity: WarningSeverity;
  category: string;
  title: string;
  details: string;
}

// ── Spam trigger words/phrases (case-insensitive) ──
const SPAM_TRIGGER_WORDS_HIGH = [
  'act now',
  'buy now',
  'buy direct',
  'order now',
  'limited time',
  'offer expires',
  'urgent',
  'instant',
  'free access',
  'free gift',
  'free trial',
  'risk free',
  'no obligation',
  'no cost',
  'no fees',
  'double your',
  'earn extra cash',
  'extra income',
  'make money',
  'million dollars',
  'cash bonus',
  'credit card',
  'debt',
  'winner',
  'you have been selected',
  'congratulations',
  'click here',
  'click below',
  'once in a lifetime',
  'this is not spam',
  'not junk',
  'not spam',
];

const SPAM_TRIGGER_WORDS_MEDIUM = [
  'free',
  'guarantee',
  'no strings attached',
  'special promotion',
  'exclusive deal',
  'lowest price',
  'best price',
  'discount',
  'save big',
  'bargain',
  'bonus',
  'compare',
  'offer',
  'deal',
  'prize',
  'reward',
  'sale',
  'clearance',
  'affordable',
  'cheap',
  'lowest rate',
  'price match',
  'amazing',
  'incredible',
  'unbelievable',
  'as seen on',
  'endorsed by',
  'opt in',
  'subscribe',
  'sign up now',
  'dear friend',
  'dear sir',
];

// ── URL shortener domains ──
const URL_SHORTENERS = [
  'bit.ly',
  'tinyurl.com',
  't.co',
  'goo.gl',
  'ow.ly',
  'is.gd',
  'buff.ly',
  'adf.ly',
  'bl.ink',
  'lnkd.in',
  'shorte.st',
  'mcaf.ee',
  'su.pr',
  'tiny.cc',
];

export function analyzeEmailDeliverability(html: string, subject: string): DeliverabilityWarning[] {
  const warnings: DeliverabilityWarning[] = [];
  const lowerHtml = html.toLowerCase();
  const lowerSubject = subject.toLowerCase();

  // Helper to strip HTML tags for text analysis
  const textContent = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const lowerText = textContent.toLowerCase();

  // ── 1. Subject line checks ──

  if (!subject.trim()) {
    warnings.push({
      id: 'no-subject',
      severity: 'high',
      category: 'Subject Line',
      title: 'Missing subject line',
      details: 'Emails without a subject line are almost always flagged as spam.',
    });
  }

  if (subject.length > 0 && subject === subject.toUpperCase() && /[A-Z]/.test(subject)) {
    warnings.push({
      id: 'subject-all-caps',
      severity: 'high',
      category: 'Subject Line',
      title: 'Subject line is ALL CAPS',
      details: 'ALL CAPS subject lines are a major spam signal. Use normal capitalization instead.',
    });
  }

  if ((subject.match(/!/g) || []).length >= 2) {
    warnings.push({
      id: 'subject-exclamation',
      severity: 'medium',
      category: 'Subject Line',
      title: 'Multiple exclamation marks in subject',
      details: 'Excessive exclamation marks trigger spam filters. Use at most one.',
    });
  }

  if (subject.length > 60) {
    warnings.push({
      id: 'subject-too-long',
      severity: 'low',
      category: 'Subject Line',
      title: 'Subject line may be truncated',
      details: `Your subject is ${subject.length} characters. Many email clients truncate after 50-60 characters.`,
    });
  }

  // Check subject for spam words
  for (const word of SPAM_TRIGGER_WORDS_HIGH) {
    if (lowerSubject.includes(word)) {
      warnings.push({
        id: `subject-spam-${word.replace(/\s/g, '-')}`,
        severity: 'high',
        category: 'Spam Trigger',
        title: `Subject contains spam phrase: "${word}"`,
        details: 'This phrase is commonly flagged by spam filters. Consider rephrasing.',
      });
      break; // Only report one subject spam trigger
    }
  }

  // ── 2. Content spam word checks ──

  const foundHighSpam: string[] = [];
  for (const word of SPAM_TRIGGER_WORDS_HIGH) {
    if (lowerText.includes(word)) {
      foundHighSpam.push(word);
    }
  }
  if (foundHighSpam.length > 0) {
    warnings.push({
      id: 'content-spam-high',
      severity: 'high',
      category: 'Spam Trigger',
      title: `Content contains ${foundHighSpam.length} high-risk spam phrase(s)`,
      details: `Found: ${foundHighSpam
        .slice(0, 5)
        .map((w) => `"${w}"`)
        .join(
          ', '
        )}${foundHighSpam.length > 5 ? ` and ${foundHighSpam.length - 5} more` : ''}. Reword these to improve deliverability.`,
    });
  }

  const foundMediumSpam: string[] = [];
  for (const word of SPAM_TRIGGER_WORDS_MEDIUM) {
    if (lowerText.includes(word)) {
      foundMediumSpam.push(word);
    }
  }
  if (foundMediumSpam.length >= 3) {
    warnings.push({
      id: 'content-spam-medium',
      severity: 'medium',
      category: 'Spam Trigger',
      title: `Content uses ${foundMediumSpam.length} promotional keywords`,
      details: `Found: ${foundMediumSpam
        .slice(0, 5)
        .map((w) => `"${w}"`)
        .join(', ')}. Using too many promotional words can look spammy.`,
    });
  }

  // ── 3. ALL CAPS in body ──

  const capsWords = textContent.match(/\b[A-Z]{4,}\b/g) || [];
  // Filter out common abbreviations
  const realCapsWords = capsWords.filter(
    (w) => !['HTML', 'CSS', 'URL', 'HTTP', 'HTTPS', 'API', 'CTA', 'CEO', 'FAQ', 'PDF'].includes(w)
  );
  if (realCapsWords.length >= 3) {
    warnings.push({
      id: 'body-all-caps',
      severity: 'medium',
      category: 'Formatting',
      title: 'Multiple ALL CAPS words in content',
      details: `Found ${realCapsWords.length} ALL CAPS words (e.g. "${realCapsWords[0]}"). Excessive capitalization triggers spam filters.`,
    });
  }

  // ── 4. Excessive exclamation marks in body ──

  const exclamationCount = (textContent.match(/!/g) || []).length;
  if (exclamationCount >= 5) {
    warnings.push({
      id: 'body-exclamation',
      severity: 'medium',
      category: 'Formatting',
      title: 'Too many exclamation marks',
      details: `Found ${exclamationCount} exclamation marks. Keep it to 1-2 per email for best deliverability.`,
    });
  }

  // ── 5. Missing unsubscribe link ──

  if (
    !lowerHtml.includes('unsubscribe') &&
    !lowerHtml.includes('opt-out') &&
    !lowerHtml.includes('opt out') &&
    !lowerHtml.includes('manage preferences')
  ) {
    warnings.push({
      id: 'no-unsubscribe',
      severity: 'high',
      category: 'Compliance',
      title: 'No unsubscribe link detected',
      details:
        'An unsubscribe link is legally required (CAN-SPAM, GDPR). Emails without one are likely to be flagged as spam and may violate regulations.',
    });
  }

  // ── 6. Image-to-text ratio ──

  const imageCount = (lowerHtml.match(/<img\b/g) || []).length;
  const wordCount = textContent.split(/\s+/).filter((w) => w.length > 0).length;

  if (imageCount > 0 && wordCount < 50) {
    warnings.push({
      id: 'low-text-ratio',
      severity: 'high',
      category: 'Content',
      title: 'Low text-to-image ratio',
      details: `Found ${imageCount} image(s) but only ~${wordCount} words. Emails that are mostly images get flagged as spam. Add more text content.`,
    });
  }

  if (imageCount === 0 && wordCount < 20) {
    warnings.push({
      id: 'too-little-content',
      severity: 'medium',
      category: 'Content',
      title: 'Very little content',
      details:
        'Your email has minimal content. Very short emails may look suspicious to spam filters.',
    });
  }

  // ── 7. Missing alt text on images ──

  const imgTagsWithoutAlt = lowerHtml.match(/<img(?![^>]*\balt\s*=)[^>]*>/g) || [];
  if (imgTagsWithoutAlt.length > 0) {
    warnings.push({
      id: 'missing-alt-text',
      severity: 'medium',
      category: 'Accessibility',
      title: `${imgTagsWithoutAlt.length} image(s) missing alt text`,
      details:
        'Images without alt text hurt accessibility and can trigger spam filters. Always provide descriptive alt text.',
    });
  }

  // ── 8. URL shorteners ──

  for (const shortener of URL_SHORTENERS) {
    if (lowerHtml.includes(shortener)) {
      warnings.push({
        id: 'url-shortener',
        severity: 'high',
        category: 'Links',
        title: 'URL shortener detected',
        details: `Found "${shortener}" link. URL shorteners are a major spam signal because they hide the true destination. Use full URLs instead.`,
      });
      break;
    }
  }

  // ── 9. JavaScript ──

  if (lowerHtml.includes('<script') || lowerHtml.includes('javascript:')) {
    warnings.push({
      id: 'javascript',
      severity: 'high',
      category: 'Content',
      title: 'JavaScript detected',
      details:
        'JavaScript is stripped by all email clients and is a major spam trigger. Remove all scripts.',
    });
  }

  // ── 10. Form elements ──

  if (
    lowerHtml.includes('<form') ||
    lowerHtml.includes('<input') ||
    lowerHtml.includes('<select')
  ) {
    warnings.push({
      id: 'form-elements',
      severity: 'medium',
      category: 'Content',
      title: 'Form elements detected',
      details:
        'HTML forms are not supported in most email clients and can trigger spam filters. Use links to web forms instead.',
    });
  }

  // ── 11. Hidden text ──

  if (
    lowerHtml.includes('display:none') ||
    lowerHtml.includes('display: none') ||
    lowerHtml.includes('visibility:hidden') ||
    lowerHtml.includes('visibility: hidden') ||
    lowerHtml.includes('font-size:0') ||
    lowerHtml.includes('font-size: 0')
  ) {
    warnings.push({
      id: 'hidden-text',
      severity: 'high',
      category: 'Content',
      title: 'Hidden text detected',
      details:
        'Hidden text (display:none, visibility:hidden, font-size:0) is a classic spam technique and will trigger filters.',
    });
  }

  // ── 12. HTML size (Gmail clips at ~102KB) ──

  const htmlSizeKB = new Blob([html]).size / 1024;
  if (htmlSizeKB > 102) {
    warnings.push({
      id: 'html-too-large',
      severity: 'high',
      category: 'Size',
      title: 'Email HTML exceeds 102KB',
      details: `Your email is ${htmlSizeKB.toFixed(0)}KB. Gmail clips emails over 102KB and shows a "View entire message" link, which hurts engagement.`,
    });
  } else if (htmlSizeKB > 80) {
    warnings.push({
      id: 'html-large',
      severity: 'medium',
      category: 'Size',
      title: 'Email HTML is getting large',
      details: `Your email is ${htmlSizeKB.toFixed(0)}KB. Gmail clips at 102KB. Consider reducing content or image sizes.`,
    });
  }

  // ── 13. Excessive links ──

  const linkCount = (lowerHtml.match(/<a\b/g) || []).length;
  if (linkCount > 20) {
    warnings.push({
      id: 'too-many-links',
      severity: 'medium',
      category: 'Links',
      title: `${linkCount} links detected`,
      details: 'Too many links can trigger spam filters. Keep links under 15-20 for best results.',
    });
  }

  // ── 14. Background images ──

  if (
    lowerHtml.includes('background-image') ||
    lowerHtml.includes('background:url') ||
    lowerHtml.includes('background: url')
  ) {
    warnings.push({
      id: 'background-image',
      severity: 'low',
      category: 'Compatibility',
      title: 'CSS background images used',
      details:
        'Background images are not supported in Outlook and some other email clients. Use <img> tags instead for important images.',
    });
  }

  // ── 15. <style> tags ──

  const styleTagCount = (lowerHtml.match(/<style\b/g) || []).length;
  if (styleTagCount > 0) {
    warnings.push({
      id: 'style-tags',
      severity: 'low',
      category: 'Compatibility',
      title: `${styleTagCount} <style> tag(s) found`,
      details:
        'Some email clients (Gmail, older Outlook) strip <style> tags entirely. Use inline styles for consistent rendering.',
    });
  }

  // ── 16. Colored text on colored background (readability) ──

  if (
    (lowerHtml.includes('color:#fff') ||
      lowerHtml.includes('color: #fff') ||
      lowerHtml.includes('color:white') ||
      lowerHtml.includes('color: white')) &&
    !lowerHtml.includes('background')
  ) {
    warnings.push({
      id: 'white-text',
      severity: 'medium',
      category: 'Content',
      title: 'White text without visible background',
      details:
        'White text without a dark background may be invisible and look like a spam obfuscation technique.',
    });
  }

  // ── 17. Money symbols ──

  const moneySymbolCount = (textContent.match(/[$\u20AC\u00A3\u00A5]/g) || []).length;
  if (moneySymbolCount >= 4) {
    warnings.push({
      id: 'money-symbols',
      severity: 'low',
      category: 'Spam Trigger',
      title: 'Multiple currency symbols',
      details: `Found ${moneySymbolCount} currency symbols. Excessive use of $, EUR, GBP, or JPY symbols can look spammy.`,
    });
  }

  return warnings;
}

/** Returns a summary score from 0-100 (100 = excellent deliverability) */
export function getDeliverabilityScore(warnings: DeliverabilityWarning[]): number {
  let score = 100;
  for (const w of warnings) {
    switch (w.severity) {
      case 'high':
        score -= 15;
        break;
      case 'medium':
        score -= 8;
        break;
      case 'low':
        score -= 3;
        break;
    }
  }
  return Math.max(0, Math.min(100, score));
}
